<template>
  <div class="provider-agreement-list-page">
    <div class="top-bar">
      <PageTitle hideMarginLeft>Договоры поставок</PageTitle>
    </div>
    <CustomTable 
      title="Мои договоры поставок" 
      :rowsData="rowsData" 
      :columns="columns" 
      :loading="isLoading"
      :small="true"
      :show-search="true"
      :custom-body="true"
      :sticky-header="true"
      :height="300"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.contractor_agreement_list_id }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.contractor_name || '-' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.agreement_num || '-' }}
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.document_date ? formatDate(props.row.document_date) : '-' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.sbs_project_name || '-' }}
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <!-- Если документ из BINECT -->
            <div v-if="props.row.is_from_binect" class="sign-binect-badge">
              <q-badge color="blue-grey" label="Подписан в BINECT" />
            </div>
            
            <!-- Обычное отображение статуса подписей -->
            <div v-else class="sign-status-container">
              <!-- Иконки статуса подписей -->
              <div class="sign-status-icons">
                <!-- Застройщик -->
                <div class="sign-status-row">
                  <q-icon name="domain" size="16px" color="grey-7" />
                  <q-icon 
                    :name="getSignIcon(props.row.contractor_sign_status).icon"
                    :color="getSignIcon(props.row.contractor_sign_status).color"
                    size="16px"
                  />
                  <q-tooltip>
                    Застройщик: {{ getSignLabel(props.row.contractor_sign_status) }}
                    <span v-if="props.row.contractor_signed_at">
                      <br>{{ formatDateTime(props.row.contractor_signed_at) }}
                    </span>
                  </q-tooltip>
                </div>
                <!-- Поставщик -->
                <div class="sign-status-row">
                  <q-icon name="local_shipping" size="16px" color="grey-7" />
                  <q-icon 
                    :name="getSignIcon(props.row.provider_sign_status).icon"
                    :color="getSignIcon(props.row.provider_sign_status).color"
                    size="16px"
                  />
                  <q-tooltip>
                    Поставщик: {{ getSignLabel(props.row.provider_sign_status) }}
                    <span v-if="props.row.provider_signed_at">
                      <br>{{ formatDateTime(props.row.provider_signed_at) }}
                    </span>
                  </q-tooltip>
                </div>
              </div>

              <!-- Информация о следующем шаге -->
              <div v-if="props.row.next_sign_description && !props.row.is_fully_signed" class="sign-next-step">
                <q-icon name="info" size="14px" color="info" />
                <span class="sign-next-step-text">{{ props.row.next_sign_description }}</span>
              </div>
            </div>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <CustomDropdown
              :loading="actionLoading[props.row.contractor_agreement_list_id]"
              @click="(action) => handleAction(action, props.row)"
              :options="getActionsDropdown(props.row)"
              title="Действие"
            />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import PageTitle from "@/components/common/PageTitle.vue";
import CustomDropdown from "@/components/common/CustomDropdown";
import {
  getProviderAgreementList,
  providerAgreementListColumns
} from "@/views/ProviderAgreementList/services";
import { providerAgreementListAPI } from "@/views/ProviderAgreementList/services/api";

export default {
  name: "ProviderAgreementList",
  components: {
    CustomDropdown,
    CustomTable,
    CustomTableCell,
    PageTitle
  },
  data() {
    return {
      rowsData: [],
      isLoading: false,
      columns: providerAgreementListColumns,
      actionLoading: {},
    };
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      const res = await getProviderAgreementList();
      this.isLoading = false;
      if (!res) return;
      this.rowsData = res;
    },
    async loadDataSilently() {
      const res = await getProviderAgreementList();
      if (res) {
        this.rowsData = res;
      }
    },
    getActionsDropdown(row) {
      // Используем поля из бэкенда
      const canSign = row?.can_sign === true;
      const canDownload = row?.can_download_agreement !== false;
      const isSigned = row?.provider_sign_status === 'SIGNED';
      const isFromBinect = row?.is_from_binect === true;

      // Определяем hint для подписания
      let signHint = '';
      if (isFromBinect) {
        signHint = 'Договор подписан в системе BINECT';
      } else if (isSigned) {
        signHint = 'Договор уже подписан';
      } else if (!canSign) {
        signHint = 'Подписание недоступно';
      }

      return [
        {
          label: 'Скачать договор',
          action: 'download',
          icon: 'download',
          disabled: !canDownload,
          hint: !canDownload ? 'Скачивание недоступно' : ''
        },
        {
          label: isSigned ? 'Подписан' : 'Подписать',
          action: 'sign',
          icon: 'verified',
          disabled: !canSign,
          hint: signHint
        },
      ];
    },
    async handleAction(action, row) {
      if (action?.disabled) return;

      const id = row.contractor_agreement_list_id;
      
      switch (action.action) {
        case 'download':
          this.actionLoading[id] = true;
          try {
            await this.downloadAgreement(row);
          } finally {
            this.actionLoading[id] = false;
          }
          break;
        case 'sign':
          this.actionLoading[id] = true;
          try {
            await this.signAgreement(row);
          } finally {
            this.actionLoading[id] = false;
          }
          break;
      }
    },
    async downloadAgreement(row) {
      try {
        const response = await providerAgreementListAPI.downloadAgreement(row.contractor_agreement_list_id);
        const { saveAs } = await import('file-saver');
        const blob = new Blob([response.data], {
          type: 'application/pdf'
        });

        const agreementNum = row.agreement_num || row.contractor_agreement_list_id;
        const filename = `Договор поставки №${agreementNum}.pdf`;
        saveAs(blob, filename);
        this.showSucceedSnackbar('Договор успешно скачан');
      } catch (e) {
        console.error('Error downloading agreement:', e);
        this.showErrorSnackbar('Ошибка при скачивании договора');
      }
    },
    async signAgreement(row) {
      try {
        const res = await providerAgreementListAPI.sign(row.contractor_agreement_list_id);
        const signUrl = res?.sign_url;

        if (!signUrl) {
          this.showErrorSnackbar('Сервис подписи не вернул ссылку для подписания');
          return;
        }

        // Переход на страницу подписания NCALayer
        window.location.href = signUrl;
      } catch (e) {
        console.error('Error creating sign process:', e);
        this.showErrorSnackbar(e?.response?.data?.error || 'Ошибка при создании процесса подписания');
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('ru-RU');
      } catch (e) {
        return dateStr;
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
      } catch (e) {
        return dateStr;
      }
    },
    getSignLabel(status) {
      if (status === 'SIGNED') return 'Подписан';
      if (status === 'WAITING') return 'На подписи';
      if (!status || status === 'NOT_STARTED') return 'Не подписан';
      return status;
    },
    getSignIcon(status) {
      if (status === 'SIGNED') {
        return { icon: 'check_circle', color: 'positive' };
      }
      if (status === 'WAITING') {
        return { icon: 'hourglass_empty', color: 'warning' };
      }
      return { icon: 'cancel', color: 'grey-5' };
    },
  },
  mounted() {
    this.loadData();
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
};
</script>

<style scoped lang="scss">
.provider-agreement-list-page {
  padding: 8px;
}

.top-bar {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  :deep(.title) {
    margin-bottom: 0 !important;
  }
}

.sign-binect-badge {
  display: flex;
  justify-content: center;
}

.sign-status-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  width: 100%;
  padding: 4px 0;
}

.sign-status-icons {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
}

.sign-status-row {
  display: flex;
  align-items: center;
  gap: 2px;
  position: relative;
  cursor: default;
}

.sign-next-step {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #1976d2;
  max-width: 180px;
}

.sign-next-step-text {
  text-align: center;
  line-height: 1.2;
}
</style>

